import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Search, Loader2, Trash2, Pencil, FileDown, Copy, Download, CalendarDays, Printer, Eye, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api, { formatApiError, API } from "@/lib/api";
import CustomerSearchSelect from "@/components/CustomerSearchSelect";
import { CreatorBadge } from "@/components/CreatorBadge";
import { deriveState } from "@/lib/gstStates";
import { useAuth } from "@/context/AuthContext";
import InvoiceItemRow from "@/components/invoices/InvoiceItemRow";
import InvoicePreviewDialog from "@/components/invoices/InvoicePreviewDialog";
import { rupee, r2, uid } from "@/lib/format";

const blankItem = () => ({ uid: uid(), item_master_id: "", inventory_id: "", item_name: "", hsn: "", description: "", qty: 1, unit: "Nos", unit_price: "" });
const blankForm = () => ({
  customer_name: "", customer_address: "", mobile: "", email: "", customer_gst: "",
  customer_state: "", customer_state_code: "", customer_id: "", invoice_date: new Date().toISOString().slice(0, 10),
  gst_rate: 18, gst_inclusive: false, notes: "", items: [blankItem()], company_state_code: "",
});

export default function Invoices() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blankForm());
  const [nextNo, setNextNo] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkMonth, setBulkMonth] = useState(new Date().toISOString().slice(0, 7));
  const [preview, setPreview] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [emailing, setEmailing] = useState(false);
  const [companyStateCode, setCompanyStateCode] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const downloadExport = (path, filename) => {
    const token = localStorage.getItem("access_token");
    fetch(`${API}/${path}`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => { if (!r.ok) throw new Error("no data"); return r.blob(); })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
        toast.success("Download started");
      })
      .catch(() => toast.error("No invoices found for that month"));
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      const { data } = await api.get("/invoices", { params });
      setItems(data);
      setSelectedIds(new Set());
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchItems(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => { const t = setTimeout(fetchItems, 250); return () => clearTimeout(t); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [search]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/store");
        const code = data.state_code || deriveState(data.gstin).code || "";
        setCompanyStateCode(code);
      } catch (e) { console.error("store settings fetch fail", e); }
    })();
  }, []);

  const openNew = async () => {
    setForm({ ...blankForm(), gst_rate: 18 });
    setEditingId(null);
    setOpen(true);
    try {
      const [{ data: nn }, { data: cfg }] = await Promise.all([
        api.get("/invoices/next-no"), api.get("/settings/invoice"),
      ]);
      setNextNo(nn.invoice_no);
      setForm((f) => ({ ...f, gst_rate: cfg.default_gst_rate ?? 18 }));
    } catch (e) { setNextNo(""); }
  };

  const openEdit = (inv) => {
    setEditingId(inv.id);
    setNextNo(inv.invoice_no);
    setForm({
      customer_name: inv.customer_name || "", customer_address: inv.customer_address || "",
      mobile: inv.mobile || "", email: inv.email || "", customer_gst: inv.customer_gst || "",
      customer_state: inv.customer_state || "", customer_state_code: inv.customer_state_code || "",
      customer_id: inv.customer_id || "", invoice_date: inv.invoice_date || new Date().toISOString().slice(0, 10),
      gst_rate: inv.gst_rate ?? 18, gst_inclusive: !!inv.gst_inclusive, notes: inv.notes || "",
      items: (inv.items || []).map((it) => ({
        uid: uid(), item_master_id: it.item_master_id || "", inventory_id: it.inventory_id || "", item_name: it.item_name || "", hsn: it.hsn || "", description: it.description || "",
        qty: it.qty ?? 1, unit: it.unit || "Nos", unit_price: it.unit_price ?? "",
      })),
    });
    setOpen(true);
  };

  const openDuplicate = async (inv) => {
    setEditingId(null);
    setForm({
      customer_name: inv.customer_name || "", customer_address: inv.customer_address || "",
      mobile: inv.mobile || "", email: inv.email || "", customer_gst: inv.customer_gst || "",
      customer_state: inv.customer_state || "", customer_state_code: inv.customer_state_code || "",
      customer_id: inv.customer_id || "", invoice_date: new Date().toISOString().slice(0, 10),
      gst_rate: inv.gst_rate ?? 18, gst_inclusive: !!inv.gst_inclusive, notes: inv.notes || "",
      items: (inv.items || []).map((it) => ({
        uid: uid(), item_master_id: it.item_master_id || "", inventory_id: "", item_name: it.item_name || "", hsn: it.hsn || "", description: it.description || "",
        qty: it.qty ?? 1, unit: it.unit || "Nos", unit_price: it.unit_price ?? "",
      })),
    });
    setOpen(true);
    try { const { data } = await api.get("/invoices/next-no"); setNextNo(data.invoice_no); }
    catch (e) { setNextNo(""); }
  };

  useEffect(() => {
    if (searchParams.get("new") === "1") {
      openNew();
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  const setItem = (idx, patch) => setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));
  const addRow = () => setForm((f) => ({ ...f, items: [...f.items, blankItem()] }));
  const removeRow = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const gross = r2(form.items.reduce((a, it) => a + Number(it.qty || 0) * Number(it.unit_price || 0), 0));
  const rate = Number(form.gst_rate || 0);
  const subtotal = form.gst_inclusive ? r2(gross / (1 + rate / 100)) : gross;
  const gstTotal = r2(subtotal * rate / 100);
  const grand = r2(subtotal + gstTotal);
  const custCode = form.customer_state_code || deriveState(form.customer_gst).code;
  const isIntra = !(custCode && companyStateCode && custCode !== companyStateCode);

  const onGstChange = (val) => {
    const up = val.toUpperCase();
    const d = deriveState(up);
    setForm((f) => ({ ...f, customer_gst: up, customer_state: d.state || f.customer_state, customer_state_code: d.code || f.customer_state_code }));
  };

  const save = async () => {
    if (!form.customer_name.trim()) { toast.error("Customer name is required"); return; }
    const valid = form.items.filter((it) => (it.item_name || "").trim() || (it.description || "").trim());
    if (valid.length === 0) { toast.error("Add at least one item"); return; }
    setSaving(true);
    const payload = {
      customer_name: form.customer_name, customer_address: form.customer_address,
      mobile: form.mobile, email: form.email, customer_gst: form.customer_gst,
      customer_state: form.customer_state, customer_state_code: form.customer_state_code,
      customer_id: form.customer_id, invoice_date: form.invoice_date,
      gst_rate: Number(form.gst_rate || 0), gst_inclusive: !!form.gst_inclusive, notes: form.notes,
      items: valid.map((it) => ({
        item_master_id: it.item_master_id || "", inventory_id: it.inventory_id || "", item_name: it.item_name || "", hsn: it.hsn || "", description: it.description || "",
        qty: Number(it.qty || 0), unit: it.unit || "Nos", unit_price: Number(it.unit_price || 0),
      })),
    };
    try {
      if (editingId) {
        const { data } = await api.put(`/invoices/${editingId}`, payload);
        setItems((prev) => prev.map((x) => (x.id === editingId ? data : x)));
        setOpen(false);
        toast.success(`Invoice ${data.invoice_no} updated`);
        (data.stock_warnings || []).forEach((w) => toast.warning(w));
        setPreview(data);
      } else {
        const { data } = await api.post("/invoices", payload);
        setItems((prev) => [data, ...prev]);
        setOpen(false);
        toast.success(`Invoice ${data.invoice_no} created`);
        (data.stock_warnings || []).forEach((w) => toast.warning(w));
        setPreview(data);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    const id = deleteId; const prev = items;
    setItems((p) => p.filter((x) => x.id !== id));
    setDeleteId(null);
    try { await api.delete(`/invoices/${id}`); toast.success("Invoice deleted"); }
    catch (e) { setItems(prev); toast.error(formatApiError(e)); }
  };

  const toggleSelect = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allSelected = items.length > 0 && selectedIds.size === items.length;
  const toggleSelectAll = () => setSelectedIds(allSelected ? new Set() : new Set(items.map((x) => x.id)));

  const confirmBulkDelete = async () => {
    const ids = Array.from(selectedIds); const prev = items;
    setItems((p) => p.filter((x) => !selectedIds.has(x.id)));
    setBulkConfirm(false);
    try {
      const { data } = await api.post("/invoices/bulk-delete", { ids });
      setSelectedIds(new Set());
      toast.success(`${data.deleted} invoice(s) deleted`);
    } catch (e) { setItems(prev); toast.error(formatApiError(e)); }
  };

  const openPreview = (inv) => { setPreview(inv); setEmailTo(inv.email || ""); };

  const downloadPdf = async (path, filename) => {
    const token = localStorage.getItem("access_token");
    try {
      const r = await fetch(`${API}/${path}`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!r.ok) throw new Error("fail");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download PDF"); }
  };

  const sendWhatsApp = async (inv) => {
    try {
      const { data } = await api.post(`/invoices/${inv.id}/share`);
      const url = `${API}/invoices/public/${data.token}`;
      const num = (inv.mobile || "").replace(/\D/g, "");
      const wa = num.length === 10 ? `91${num}` : num;
      const msg = `Please find the attached invoice for your reference.\n${url}`;
      const link = wa ? `https://wa.me/${wa}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`;
      window.open(link, "_blank", "noopener");
      toast.success("Opening WhatsApp… link valid for 7 days");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const sendEmail = async () => {
    if (!preview) return;
    setEmailing(true);
    try {
      const { data } = await api.post(`/invoices/${preview.id}/email`, { to: emailTo });
      toast.success(`Invoice emailed to ${data.to}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setEmailing(false); }
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-wider text-[#64748B] mb-1">Module 05</div>
          <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight">Tax Invoices</h1>
          <p className="text-sm text-[#475569] mt-1">Create GST-compliant tax invoices with CGST/SGST or IGST, then download an A4 PDF.</p>
        </div>
        <Button onClick={openNew} className="bg-[#0055FF] hover:bg-[#0040C1] text-white gap-2" data-testid="add-invoice-btn">
          <Plus className="w-4 h-4" /> New invoice
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
          <Input placeholder="Search by invoice no, customer, mobile…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10" data-testid="invoices-search" />
        </div>
        {isAdmin && (
          <Button variant="outline" onClick={() => setBulkOpen(true)} className="h-10 gap-2" data-testid="bulk-download-btn">
            <Download className="w-4 h-4" /> Bulk download
          </Button>
        )}
        {selectedIds.size > 0 && (
          <Button variant="outline" onClick={() => setBulkConfirm(true)} className="h-10 gap-2 text-[#EF4444] border-[#EF4444]/40 hover:bg-red-50" data-testid="bulk-delete-invoices-btn">
            <Trash2 className="w-4 h-4" /> Delete ({selectedIds.size})
          </Button>
        )}
      </div>

      <div className="border border-[#E2E8F0] rounded-lg bg-white overflow-x-auto">
        <Table>
          <TableHeader className="bg-[#F8F9FA]">
            <TableRow>
              <TableHead className="w-10">
                <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} aria-label="Select all" data-testid="invoices-select-all" />
              </TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Invoice No</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Customer</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider text-center">Tax</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider text-right">Grand Total</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Date</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Created By</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-sm text-[#64748B]">
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
              </TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-sm text-[#64748B]" data-testid="invoices-empty">
                No invoices yet.
              </TableCell></TableRow>
            ) : items.map((inv) => (
              <TableRow key={inv.id} data-testid={`invoice-row-${inv.invoice_no}`} data-state={selectedIds.has(inv.id) ? "selected" : undefined}>
                <TableCell>
                  <Checkbox checked={selectedIds.has(inv.id)} onCheckedChange={() => toggleSelect(inv.id)} aria-label="Select row" data-testid={`invoice-select-${inv.invoice_no}`} />
                </TableCell>
                <TableCell className="font-mono text-sm font-medium">{inv.invoice_no}</TableCell>
                <TableCell>
                  <div className="text-sm">{inv.customer_name}</div>
                  <div className="font-mono text-[11px] text-[#64748B]">{inv.mobile}</div>
                </TableCell>
                <TableCell className="text-center">
                  <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#EEF2FA] text-[#334155]">
                    {inv.intra_state ? "CGST+SGST" : "IGST"}
                  </span>
                </TableCell>
                <TableCell className="text-right font-mono text-sm font-semibold">{rupee(inv.grand_total)}</TableCell>
                <TableCell className="font-mono text-xs text-[#64748B]">{inv.invoice_date || (inv.created_at || "").slice(0, 10)}</TableCell>
                <TableCell><CreatorBadge name={inv.created_by} testId={`invoice-creator-${inv.invoice_no}`} /></TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openPreview(inv)} title="Preview" data-testid={`preview-invoice-${inv.invoice_no}`}>
                      <Eye className="w-4 h-4 text-[#0F766E]" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(inv)} title="Edit invoice" data-testid={`edit-invoice-${inv.invoice_no}`}>
                      <Pencil className="w-4 h-4 text-[#0055FF]" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openDuplicate(inv)} title="Duplicate invoice" data-testid={`duplicate-invoice-${inv.invoice_no}`}>
                      <Copy className="w-4 h-4 text-[#7C3AED]" />
                    </Button>
                    <Link to={`/invoices/${inv.id}/print?print=a5`} target="_blank" rel="noopener" data-testid={`print-invoice-${inv.invoice_no}`}>
                      <Button variant="ghost" size="sm" title="Print (A5)"><Printer className="w-4 h-4 text-[#16A34A]" /></Button>
                    </Link>
                    <Button variant="ghost" size="sm" onClick={() => downloadPdf(`invoices/${inv.id}/pdf`, `${(inv.invoice_no || "invoice").replace(/\//g, "-")}.pdf`)} title="Download PDF (A4)" data-testid={`download-invoice-${inv.invoice_no}`}>
                      <FileDown className="w-4 h-4 text-[#0055FF]" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => sendWhatsApp(inv)} title="Send via WhatsApp Web" data-testid={`whatsapp-invoice-${inv.invoice_no}`}>
                      <MessageCircle className="w-4 h-4 text-[#16A34A]" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteId(inv.id)} data-testid={`delete-invoice-${inv.invoice_no}`}>
                      <Trash2 className="w-4 h-4 text-[#EF4444]" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[96vw] w-[96vw] xl:max-w-[1400px] h-[94vh] flex flex-col overflow-hidden" data-testid="invoice-dialog">
          <DialogHeader className="shrink-0">
            <DialogTitle className="font-display tracking-tight">{editingId ? "Edit invoice" : "New tax invoice"}</DialogTitle>
            <DialogDescription>
              Invoice No: <span className="font-mono text-[#0055FF]">{nextNo || "…"}</span> ·{" "}
              <span className="font-mono">{isIntra ? "CGST + SGST (intra-state)" : "IGST (inter-state)"}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,3fr)_minmax(0,7fr)] gap-5 items-stretch flex-1 min-h-0 overflow-y-auto lg:overflow-hidden">
            {/* LEFT ~30% — customer, invoice details & totals */}
            <div className="space-y-3 lg:pr-5 lg:border-r lg:border-[#E2E8F0] lg:h-full lg:overflow-y-auto">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-[#475569]">Existing customer (optional)</Label>
                <CustomerSearchSelect
                  selectedId={form.customer_id || null}
                  onPick={(c) => setForm((f) => c
                    ? ({ ...f, customer_id: c.id, customer_name: c.name, mobile: c.mobile, email: c.email || "", customer_gst: c.gstin || "", ...deriveState(c.gstin).code ? { customer_state: deriveState(c.gstin).state, customer_state_code: deriveState(c.gstin).code } : {} })
                    : ({ ...f, customer_id: "", customer_name: "", mobile: "", email: "", customer_gst: "", customer_state: "", customer_state_code: "" }))}
                  placeholder="Search customers by name or mobile…"
                  testId="invoice-customer-picker"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1 sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wide text-[#475569]">Customer Name *</Label>
                  <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="h-8" data-testid="invoice-customer-name" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wide text-[#475569]">Mobile</Label>
                  <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="h-8 font-mono" data-testid="invoice-mobile" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wide text-[#475569]">Invoice Date</Label>
                  <Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} className="h-8 font-mono" data-testid="invoice-date" />
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wide text-[#475569]">Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-8" data-testid="invoice-email" />
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wide text-[#475569]">Customer Address</Label>
                  <Textarea rows={2} value={form.customer_address} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} className="min-h-[56px] text-sm" data-testid="invoice-customer-address" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wide text-[#475569]">Customer GSTIN</Label>
                  <Input value={form.customer_gst} onChange={(e) => onGstChange(e.target.value)} className="h-8 font-mono" placeholder="29ABCDE1234F1Z5" maxLength={15} data-testid="invoice-customer-gst" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wide text-[#475569]">State / Code</Label>
                  <Input value={form.customer_state ? `${form.customer_state} (${form.customer_state_code})` : ""} onChange={(e) => setForm({ ...form, customer_state: e.target.value, customer_state_code: "" })} className="h-8" placeholder="Auto from GSTIN" data-testid="invoice-customer-state" />
                </div>
              </div>

              <div className="rounded-md border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2.5 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-[#475569]">Prices are</span>
                  <div className="inline-flex rounded-md border border-[#E2E8F0] overflow-hidden">
                    <button type="button" onClick={() => setForm({ ...form, gst_inclusive: false })}
                      className={`px-3 h-7 text-xs font-medium ${!form.gst_inclusive ? "bg-[#0055FF] text-white" : "bg-white text-[#475569]"}`} data-testid="invoice-gst-exclusive">Exclusive</button>
                    <button type="button" onClick={() => setForm({ ...form, gst_inclusive: true })}
                      className={`px-3 h-7 text-xs font-medium ${form.gst_inclusive ? "bg-[#0055FF] text-white" : "bg-white text-[#475569]"}`} data-testid="invoice-gst-inclusive">Inclusive</button>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-[#475569]">GST %</span>
                  <Input type="number" min="0" step="0.5" value={form.gst_rate} onChange={(e) => setForm({ ...form, gst_rate: e.target.value })} className="font-mono h-7 w-24 text-right" data-testid="invoice-gst-rate" />
                </div>
              </div>

              <div className="space-y-1.5 text-sm border border-[#E2E8F0] rounded-md p-3 bg-white">
                <div className="flex justify-between"><span className="text-[#64748B]">Taxable Value</span><span className="font-mono" data-testid="invoice-subtotal">{rupee(subtotal)}</span></div>
                {isIntra ? (
                  <>
                    <div className="flex justify-between"><span className="text-[#64748B]">CGST @ {r2(Number(form.gst_rate || 0) / 2)}%</span><span className="font-mono">{rupee(r2(gstTotal / 2))}</span></div>
                    <div className="flex justify-between"><span className="text-[#64748B]">SGST @ {r2(Number(form.gst_rate || 0) / 2)}%</span><span className="font-mono">{rupee(r2(gstTotal - r2(gstTotal / 2)))}</span></div>
                  </>
                ) : (
                  <div className="flex justify-between"><span className="text-[#64748B]">IGST @ {Number(form.gst_rate || 0)}%</span><span className="font-mono">{rupee(gstTotal)}</span></div>
                )}
                <div className="flex justify-between pt-1.5 border-t border-[#E2E8F0] font-semibold text-base"><span>Grand Total</span><span className="font-mono" data-testid="invoice-grand-total">{rupee(grand)}</span></div>
              </div>

              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wide text-[#475569]">Notes (optional)</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="min-h-[56px] text-sm" data-testid="invoice-notes" />
              </div>
            </div>

            {/* RIGHT ~70% — line items */}
            <div className="flex flex-col space-y-2 min-w-0 lg:h-full lg:min-h-0">
              <div className="flex items-center justify-between shrink-0">
                <div>
                  <Label className="text-xs uppercase tracking-wide text-[#475569]">Line items</Label>
                  <p className="text-[11px] text-[#94A3B8]">Items from Inventory / Item Master default to GST Inclusive.</p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addRow} className="h-8 gap-1.5 text-xs" data-testid="invoice-add-item">
                  <Plus className="w-3.5 h-3.5" /> Add item
                </Button>
              </div>
              <div className="space-y-2 overflow-x-auto flex-1 lg:overflow-y-auto lg:min-h-0 pr-1">
                {form.items.map((it, idx) => (
                  <InvoiceItemRow
                    key={it.uid || idx}
                    it={it}
                    idx={idx}
                    canRemove={form.items.length > 1}
                    onChange={(patch) => setItem(idx, patch)}
                    onRemove={() => removeRow(idx)}
                    onPick={(r) => { setItem(idx, r.kind === "inventory"
                      ? { inventory_id: r.id, item_master_id: "", item_name: r.label, hsn: r.hsn || "", description: r.description || "", unit_price: r.rate || it.unit_price }
                      : { item_master_id: r.id, inventory_id: "", item_name: r.label, hsn: r.hsn || "", description: r.description || "", unit_price: r.rate ?? "" }); setForm((f) => ({ ...f, gst_inclusive: true })); }}
                  />
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 pt-2 shrink-0 border-t border-[#E2E8F0] mt-0">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="invoice-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#0055FF] hover:bg-[#0040C1] text-white" data-testid="invoice-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingId ? "Save changes" : "Create invoice")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InvoicePreviewDialog
        preview={preview}
        onClose={() => setPreview(null)}
        onNavigate={(path) => window.open(path, "_blank", "noopener")}
        onDownload={(inv) => downloadPdf(`invoices/${inv.id}/pdf`, `${(inv.invoice_no || "invoice").replace(/\//g, "-")}.pdf`)}
        emailTo={emailTo}
        setEmailTo={setEmailTo}
        emailing={emailing}
        onSendEmail={sendEmail}
      />

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-md" data-testid="bulk-download-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2"><CalendarDays className="w-5 h-5 text-[#0055FF]" /> Bulk download (month-wise)</DialogTitle>
            <DialogDescription>Export all invoices for a month as a CSV summary or a ZIP of individual A4 PDFs.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-[#475569]">Month</Label>
              <Input type="month" value={bulkMonth} onChange={(e) => setBulkMonth(e.target.value)} className="font-mono" data-testid="bulk-month" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Button variant="outline" onClick={() => downloadExport(`exports/invoices.csv?month=${bulkMonth}`, `invoices-${bulkMonth}.csv`)} className="gap-2" data-testid="bulk-csv-btn">
                <Download className="w-4 h-4" /> CSV summary
              </Button>
              <Button onClick={() => downloadExport(`exports/invoices-pdf.zip?month=${bulkMonth}`, `invoices-${bulkMonth}.zip`)} className="bg-[#0055FF] hover:bg-[#0040C1] text-white gap-2" data-testid="bulk-pdf-zip-btn">
                <FileDown className="w-4 h-4" /> PDFs (ZIP)
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete invoice?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[#EF4444] hover:bg-[#DC2626]" data-testid="confirm-invoice-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkConfirm} onOpenChange={setBulkConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} invoice(s)?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmBulkDelete} className="bg-[#EF4444] hover:bg-[#DC2626]" data-testid="confirm-bulk-invoice-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
